import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { MapPin, Zap, RefreshCw, Globe, Ticket, CalendarPlus } from 'lucide-react';

export default function About() {
  return (
    <Layout>
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              About NextGig
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              NextGig is your plug for finding and hosting amazing events—fast, easy, and stress-free.
            </p>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16">
              {/* For Attendees */}
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <Ticket className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Looking for something to do?</h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  We help you discover events happening right where you are—from Lagos to Toronto and everywhere in between. No endless scrolling, just vibes near you.
                </p>
              </div>

              {/* For Organizers */}
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6">
                  <CalendarPlus className="w-7 h-7 text-secondary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Hosting an event?</h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  We've got you too. Promote your event, sell tickets, and get paid quickly—because waiting forever for payouts isn't it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why NextGig Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16">
              Why NextGig?
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Feature 1 */}
              <div className="text-center p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Find events near you</h3>
                <p className="text-muted-foreground text-sm">Instantly discover what's happening in your area</p>
              </div>

              {/* Feature 2 */}
              <div className="text-center p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Fast payouts</h3>
                <p className="text-muted-foreground text-sm">Organizers get paid quickly, no waiting around</p>
              </div>

              {/* Feature 3 */}
              <div className="text-center p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Simple, fair refunds</h3>
                <p className="text-muted-foreground text-sm">Hassle-free refund process when needed</p>
              </div>

              {/* Feature 4 */}
              <div className="text-center p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Built for communities</h3>
                <p className="text-muted-foreground text-sm">Growing in Nigeria, Canada & beyond</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
              To make discovering and hosting events ridiculously easy—while helping creators and communities thrive.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-32">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready for your next gig?
            </h2>
            <p className="text-muted-foreground text-lg mb-10">
              Whether you're looking to attend or host, we've got you covered.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/events">
                <Button size="lg" className="w-full sm:w-auto gap-2 h-12 px-8 text-lg" data-testid="find-events-btn">
                  <Ticket className="w-5 h-5" />
                  Find your next event
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 h-12 px-8 text-lg" data-testid="host-event-btn">
                  <CalendarPlus className="w-5 h-5" />
                  Host your own
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
